import React, { useEffect, useState } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  TablePagination,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Stack,
  Chip,
  Grid,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import api from '../../services/api';
import tesoreriaService from '../../services/tesoreriaService';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import PrintIcon from '@mui/icons-material/Print';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import BlockIcon from '@mui/icons-material/Block';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useNavigate } from 'react-router-dom';
import PDFViewer from '../shared/PDFViewer';
import AplicarSaldoReciboDialog from './AplicarSaldoReciboDialog';
import { getFileNameFromContentDisposition } from '../../utils/contentDisposition';
import { VerAsientoContableMenuItem } from '../shared/VerAsientoContableButton';
import SearchableSelect from '../SearchableSelect';
import FiltroSucursalVentaSelect from '../shared/FiltroSucursalVentaSelect';
import {
  resolveInitialFiltroNroSuc,
  resolveNroSucFiltro,
  useSucursalesVentaFiltro,
} from '../../hooks/useSucursalesVentaFiltro';
import {
  loadTesoreriaFiltros,
  TESORERIA_FILTROS_KEYS,
  usePersistTesoreriaFiltros,
} from '../../utils/tesoreriaFiltrosStorage';

const ESTADO_RECIBO_OPTIONS = [
  { value: 'activos', label: 'Activos' },
  { value: 'anulados', label: 'Anulados' },
  { value: 'todos', label: 'Todos' },
] as const;

type FiltroEstadoRecibo = (typeof ESTADO_RECIBO_OPTIONS)[number]['value'];

interface RecibosFiltrosStored {
  searchTerm?: string;
  fechaDesde?: string;
  fechaHasta?: string;
  filtroEstado?: FiltroEstadoRecibo;
  filtroNroSuc?: number | null;
  rowsPerPage?: number;
}

const loadRecibosFiltros = (): RecibosFiltrosStored => {
  const parsed = loadTesoreriaFiltros<RecibosFiltrosStored>(TESORERIA_FILTROS_KEYS.recibos);
  const estadosValidos = ESTADO_RECIBO_OPTIONS.map((o) => o.value);
  if (parsed.filtroEstado && !estadosValidos.includes(parsed.filtroEstado)) {
    parsed.filtroEstado = 'todos';
  }
  return parsed;
};

interface Recibo {
  nroFormulario: number;
  fechaEmision: string;
  nroCliente: number;
  nombre: string;
  importeTotal: number;
  formaDePago: string;
  usuario: string;
  cae_Nro: string;
  cae_Vencimiento: string;
  tipoComprobante: string;
  nroSuc: number;
  descripcion: string;
  cuit: string;
  letra?: string;
  nroComprobante?: number;
  asentado?: string;
  caeNro?: string;
  nroAsiento?: number | null;
}

interface RecibosResponse {
  items: Recibo[];
  totalCount: number;
}

const Recibos: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { enqueueSnackbar } = useSnackbar();

  // Obtener el primer y último día del mes actual
  const getFirstDayOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  };

  const getLastDayOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0);
  };

  const [recibos, setRecibos] = useState<Recibo[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => loadRecibosFiltros().rowsPerPage ?? 10);
  const [searchTerm, setSearchTerm] = useState(() => loadRecibosFiltros().searchTerm ?? '');
  const [fechaDesde, setFechaDesde] = useState<string>(() => {
    const guardado = loadRecibosFiltros().fechaDesde;
    if (guardado) return guardado;
    const fecha = getFirstDayOfMonth();
    return fecha.toISOString().split('T')[0];
  });
  const [fechaHasta, setFechaHasta] = useState<string>(() => {
    const guardado = loadRecibosFiltros().fechaHasta;
    if (guardado) return guardado;
    const fecha = getLastDayOfMonth();
    return fecha.toISOString().split('T')[0];
  });
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [selectedRecibo, setSelectedRecibo] = useState<number | null>(null);
  const [pdfFileName, setPdfFileName] = useState<string | null>(null);
  const [aplicarDialogOpen, setAplicarDialogOpen] = useState(false);
  const [aplicarDialogRecibo, setAplicarDialogRecibo] = useState<Recibo | null>(null);
  const [filtroEstado, setFiltroEstado] = useState<FiltroEstadoRecibo>(
    () => loadRecibosFiltros().filtroEstado ?? 'todos'
  );
  const [anulandoId, setAnulandoId] = useState<number | null>(null);
  const [kebabAnchor, setKebabAnchor] = useState<{ el: HTMLElement; reciboId: number } | null>(null);
  const { filtroNroSuc, setFiltroNroSuc, sucursales, loadingSucursales } = useSucursalesVentaFiltro(
    resolveInitialFiltroNroSuc(loadRecibosFiltros()),
  );

  const fetchRecibos = async (
    pageNumber: number,
    pageSize: number,
    search: string,
    desde: string,
    hasta: string,
    estado: FiltroEstadoRecibo,
    nroSuc: number | null
  ) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        enqueueSnackbar('No hay token de autenticación. Por favor, inicie sesión nuevamente.', { 
          variant: 'error',
          autoHideDuration: 3000
        });
        return;
      }

      const params: any = {
        pageNumber: pageNumber + 1,
        pageSize,
        searchTerm: search || null
      };

      if (desde) {
        params.fechaDesde = desde;
      }
      if (hasta) {
        params.fechaHasta = hasta;
      }

      if (estado === 'anulados') {
        params.asentado = 'A';
      } else if (estado === 'activos') {
        params.asentado = 'activos';
      }

      const nroSucValue = resolveNroSucFiltro(nroSuc);
      if (nroSucValue != null) {
        params.nroSuc = nroSucValue;
      }

      const response = await api.get<RecibosResponse>(
        '/api/tesoreria/recibos',
        {
          params
        }
      );

      const coerceNroAsiento = (raw: unknown): number | null => {
        if (raw == null || raw === '') return null;
        const n = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
        return Number.isFinite(n) && n > 0 ? n : null;
      };

      const mapRecibo = (item: any): Recibo => ({
        nroFormulario: (item.nroFormulario ?? item.NroFormulario ?? 0) as number,
        fechaEmision: (item.fechaEmision ?? item.FechaEmision ?? '') as string,
        nroCliente: (item.nroCliente ?? item.NroCliente ?? 0) as number,
        nombre: (item.nombre ?? item.Nombre ?? '') as string,
        importeTotal: (item.importeTotal ?? item.ImporteTotal ?? 0) as number,
        formaDePago: (item.formaDePago ?? item.FormaDePago ?? '') as string,
        usuario: (item.usuario ?? item.Usuario ?? '') as string,
        cae_Nro: (item.cae_Nro ?? item.caE_Nro ?? item.CAE_Nro ?? '') as string,
        cae_Vencimiento: (item.cae_Vencimiento ?? item.caE_Vencimiento ?? item.CAE_Vencimiento ?? '') as string,
        tipoComprobante: (item.tipoComprobante ?? item.nroTipoFormulario ?? item.NroTipoFormulario ?? '') as string,
        nroSuc: (item.nroSuc ?? item.NroSuc ?? 0) as number,
        descripcion: (item.descripcion ?? item.Descripcion ?? '') as string,
        cuit: (item.cuit ?? item.CUIT ?? '') as string,
        letra: (item.letra ?? item.Letra) as string | undefined,
        nroComprobante: (item.nroComprobante ?? item.NroComprobante) as number | undefined,
        asentado: (item.asentado ?? item.Asentado) as string | undefined,
        caeNro: (item.caeNro ?? item.CaeNro) as string | undefined,
        nroAsiento: coerceNroAsiento(item.nroAsiento ?? item.NroAsiento),
      });

      const rawItems = (response.data.items ?? (response.data as any).Items ?? []) as any[];
      setRecibos(Array.isArray(rawItems) ? rawItems.map(mapRecibo) : []);
      setTotalRecords(response.data.totalCount ?? (response.data as any).TotalCount ?? 0);
    } catch (error: any) {
      console.error('Error detallado:', error);
      const errorMessage = error.response?.data || 'Error al cargar los recibos';
      enqueueSnackbar(errorMessage, { 
        variant: 'error',
        autoHideDuration: 3000
      });
    }
  };

  usePersistTesoreriaFiltros(
    TESORERIA_FILTROS_KEYS.recibos,
    { searchTerm, fechaDesde, fechaHasta, filtroEstado, filtroNroSuc, rowsPerPage },
    [searchTerm, fechaDesde, fechaHasta, filtroEstado, filtroNroSuc, rowsPerPage]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchRecibos(page, rowsPerPage, searchTerm, fechaDesde, fechaHasta, filtroEstado, filtroNroSuc);
  }, [page, rowsPerPage, searchTerm, fechaDesde, fechaHasta, filtroEstado, filtroNroSuc]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleNuevoRecibo = () => {
    navigate('/tesoreria/recibos/nuevo');
  };

  const handleImprimirPDF = async (nroFormulario: number) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        enqueueSnackbar('No hay token de autenticación', { variant: 'error' });
        return;
      }

      const response = await api.get(
        `/api/tesoreria/recibos/${nroFormulario}/pdf`,
        {
          responseType: 'blob'
        }
      );
      
      const cd = (response.headers as any)?.['content-disposition'] as string | undefined;
      const backendFileName = getFileNameFromContentDisposition(cd);
      setPdfFileName(backendFileName);

      const url = window.URL.createObjectURL(new Blob([response.data]));
      setPdfUrl(url);
      setSelectedRecibo(nroFormulario);
    } catch (error) {
      enqueueSnackbar('Error al generar el PDF del recibo', { variant: 'error' });
    }
  };

  const handleClosePDF = () => {
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
    }
    setPdfUrl(null);
    setSelectedRecibo(null);
    setPdfFileName(null);
  };

  const handleAbrirAplicarSaldo = (recibo: Recibo) => {
    setAplicarDialogRecibo(recibo);
    setAplicarDialogOpen(true);
  };

  const handleCloseAplicarDialog = () => {
    setAplicarDialogOpen(false);
    setAplicarDialogRecibo(null);
  };

  const esReciboAnulado = (recibo: Recibo) =>
    (recibo.asentado ?? '').trim().toUpperCase() === 'A' || (recibo.nombre ?? '').trim().toUpperCase() === 'ANULADO';

  const puedeMostrarAnular = (recibo: Recibo) => {
    if (esReciboAnulado(recibo)) return false;
    const cae = (recibo.caeNro ?? recibo.cae_Nro ?? '').trim();
    return !cae;
  };

  const handleKebabOpen = (event: React.MouseEvent<HTMLElement>, reciboId: number) => {
    event.stopPropagation();
    setKebabAnchor({ el: event.currentTarget, reciboId });
  };

  const handleKebabClose = () => setKebabAnchor(null);

  const currentKebabRecibo = kebabAnchor
    ? recibos.find(r => r.nroFormulario === kebabAnchor.reciboId)
    : null;

  const renderKebabItems = (recibo: Recibo) => {
    const menuItems: React.ReactElement[] = [
      <VerAsientoContableMenuItem
        key="asiento"
        nroAsiento={recibo.nroAsiento}
        onClose={handleKebabClose}
      />,
    ];
    if (puedeMostrarAnular(recibo)) {
      menuItems.push(
        <MenuItem
          key="anular"
          onClick={() => {
            handleKebabClose();
            void handleAnular(recibo);
          }}
          disabled={anulandoId === recibo.nroFormulario}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <BlockIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Anular recibo</ListItemText>
        </MenuItem>
      );
    }
    return menuItems;
  };

  const handleAnular = async (recibo: Recibo) => {
    if (!puedeMostrarAnular(recibo)) return;

    try {
      const check = await tesoreriaService.puedeAnularRecibo(recibo.nroFormulario);
      if (!check.puedeAnular) {
        enqueueSnackbar(check.motivos[0] ?? 'No se puede anular este recibo', { variant: 'warning' });
        return;
      }
    } catch (error: any) {
      const msg = error?.response?.data?.motivos?.[0] ?? error?.response?.data?.message ?? 'No se pudo validar la anulación';
      enqueueSnackbar(msg, { variant: 'error' });
      return;
    }

    if (
      !window.confirm(
        '¿Está seguro que desea anular este recibo? Se revertirán las imputaciones a facturas y los medios de cobro asociados.'
      )
    ) {
      return;
    }

    setAnulandoId(recibo.nroFormulario);
    try {
      await tesoreriaService.anularRecibo(recibo.nroFormulario);
      enqueueSnackbar('Recibo anulado correctamente', { variant: 'success' });
      fetchRecibos(page, rowsPerPage, searchTerm, fechaDesde, fechaHasta, filtroEstado, filtroNroSuc);
    } catch (error: any) {
      const data = error?.response?.data;
      const message =
        data?.message ??
        (Array.isArray(data?.errors?.general) ? data.errors.general[0] : null) ??
        'Error al anular el recibo';
      enqueueSnackbar(message, { variant: 'error', autoHideDuration: 5000 });
    } finally {
      setAnulandoId(null);
    }
  };

  // Función para obtener el chip de estado según el CAE
  const getEstadoChip = (recibo: Recibo) => {
    if (esReciboAnulado(recibo)) {
      return {
        label: 'Anulado',
        bgColor: '#9e9e9e',
        textColor: '#ffffff',
      };
    }
    const caeNro = recibo.caeNro ?? recibo.cae_Nro;
    if (caeNro) {
      return {
        label: 'Con CAE',
        bgColor: '#4caf50',
        textColor: '#ffffff',
      };
    }
    return {
      label: 'Activo',
      bgColor: '#ff9800',
      textColor: '#ffffff',
    };
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 2
    }).format(value);
  };

  const formatComprobante = (recibo: Recibo) => {
    const { letra, nroSuc, nroComprobante } = recibo;
    if (!letra || nroSuc === undefined || nroComprobante === undefined || nroSuc === null || nroComprobante === null) {
      return '-';
    }

    const formatPart = (value: number | string, length: number) => {
      if (value === null || value === undefined) {
        return ''.padStart(length, '0');
      }
      const numeric = Number(value);
      if (!Number.isNaN(numeric)) {
        return numeric.toString().padStart(length, '0');
      }
      return String(value).padStart(length, '0');
    };

    const sucursal = formatPart(nroSuc, 5);
    const comprobante = formatPart(nroComprobante, 8);
    const letraNormalizada = letra.trim().toUpperCase();

    if (!letraNormalizada) {
      return '-';
    }

    return `${letraNormalizada}-${sucursal}-${comprobante}`;
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Tarjeta blanca contenedora principal */}
      <Card 
        sx={{ 
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Header con título y botones */}
          <Stack 
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between" 
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600,
                color: '#1976d2'
              }}
            >
              Recibos
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => navigate('/tesoreria/devoluciones')}
                sx={{ borderRadius: 2, px: 2, py: 1, textTransform: 'none', fontWeight: 500 }}
              >
                Devoluciones
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleNuevoRecibo}
                sx={{
                  borderRadius: 2,
                  px: { xs: 2, sm: 3 },
                  py: { xs: 1.5, sm: 1 },
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)',
                  },
                }}
              >
                Nuevo Recibo
              </Button>
            </Stack>
          </Stack>

          {/* Campo de búsqueda y filtros */}
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Buscar por número, descripción, nombre o CUIT..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#ffffff',
                      }
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#666' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="Fecha Desde"
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => {
                    setFechaDesde(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#ffffff',
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="Fecha Hasta"
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => {
                    setFechaHasta(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#ffffff',
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <SearchableSelect
                  label="Estado"
                  options={[...ESTADO_RECIBO_OPTIONS]}
                  value={filtroEstado}
                  onChange={(value) => {
                    if (value) {
                      setFiltroEstado(value as FiltroEstadoRecibo);
                      setPage(0);
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FiltroSucursalVentaSelect
                  value={filtroNroSuc}
                  onChange={(v) => { setFiltroNroSuc(v); setPage(0); }}
                  loading={loadingSucursales}
                  sucursales={sucursales}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Tabla o Cards según el tamaño de pantalla */}
          {isMobile ? (
            <Stack spacing={2}>
              {recibos.map((recibo) => {
                const estadoChip = getEstadoChip(recibo);
                return (
                  <Card 
                    key={recibo.nroFormulario} 
                    sx={{ 
                      width: '100%',
                      borderRadius: 2,
                      border: '1px solid #e0e0e0',
                      '&:hover': {
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    <CardContent>
                      <Stack spacing={1.5}>
                        <Stack direction="row" justifyContent="space-between" alignItems="start">
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              Recibo #{recibo.nroFormulario}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {recibo.nombre}
                            </Typography>
                          </Box>
                          <Chip
                            label={estadoChip.label}
                            size="small"
                            sx={{
                              bgcolor: estadoChip.bgColor,
                              color: estadoChip.textColor,
                              fontWeight: 500,
                            }}
                          />
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Fecha:</strong> {new Date(recibo.fechaEmision).toLocaleDateString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Cliente:</strong> {recibo.nroCliente}
                        </Typography>
                        {recibo.cuit && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>CUIT:</strong> {recibo.cuit}
                          </Typography>
                        )}
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                          <strong>Importe:</strong> {formatCurrency(recibo.importeTotal)}
                        </Typography>
                        {recibo.usuario && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Usuario:</strong> {recibo.usuario}
                          </Typography>
                        )}
                        {recibo.cae_Nro && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>CAE:</strong> {recibo.cae_Nro}
                          </Typography>
                        )}
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                          <Tooltip title="Imprimir PDF">
                            <IconButton
                              onClick={() => handleImprimirPDF(recibo.nroFormulario)}
                              size="small"
                              sx={{
                                bgcolor: '#2196f3',
                                color: '#fff',
                                borderRadius: 1.5,
                                '&:hover': { bgcolor: '#1976d2' }
                              }}
                            >
                              <PrintIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                            {!esReciboAnulado(recibo) && (
                              <Tooltip title="Aplicar saldo / Ver aplicaciones">
                                <IconButton
                                  onClick={() => handleAbrirAplicarSaldo(recibo)}
                                  size="small"
                                  sx={{
                                    bgcolor: '#2e7d32',
                                    color: '#fff',
                                    borderRadius: 1.5,
                                    '&:hover': { bgcolor: '#1b5e20' }
                                  }}
                                >
                                  <AccountBalanceWalletIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Más acciones">
                              <IconButton
                                size="small"
                                onClick={(e) => handleKebabOpen(e, recibo.nroFormulario)}
                                sx={{ borderRadius: 1.5, width: 32, height: 32 }}
                              >
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
              {recibos.length === 0 && (
                <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                  No hay recibos para mostrar
                </Typography>
              )}
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={totalRecords}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
              />
            </Stack>
          ) : (
            <TableContainer 
              component={Paper}
              sx={{
                borderRadius: 2,
                boxShadow: 'none',
                border: '1px solid #e0e0e0',
              }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Nro. Recibo</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                    <TableCell sx={{ fontWeight: 600, display: { xs: 'none', lg: 'table-cell' }, minWidth: 110 }}>Comprobante</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Cliente</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Nombre</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>CUIT</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Importe</TableCell>
                    <TableCell sx={{ fontWeight: 600, display: 'none' }}>Forma de Pago</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Usuario</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Estado</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recibos.map((recibo) => {
                    const estadoChip = getEstadoChip(recibo);
                    return (
                      <TableRow 
                        key={recibo.nroFormulario}
                        sx={{
                          '&:hover': {
                            bgcolor: '#f9f9f9',
                          },
                        }}
                      >
                        <TableCell sx={{ fontWeight: 500 }}>{recibo.nroFormulario}</TableCell>
                        <TableCell>{new Date(recibo.fechaEmision).toLocaleDateString()}</TableCell>
                        <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                          {formatComprobante(recibo)}
                        </TableCell>
                        <TableCell>{recibo.nroCliente}</TableCell>
                        <TableCell>{recibo.nombre}</TableCell>
                        <TableCell>{recibo.cuit || '-'}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: '#1976d2' }}>
                          {formatCurrency(recibo.importeTotal)}
                        </TableCell>
                        <TableCell sx={{ display: 'none' }}>{recibo.formaDePago || '-'}</TableCell>
                        <TableCell>{recibo.usuario || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={estadoChip.label}
                            size="small"
                            sx={{
                              bgcolor: estadoChip.bgColor,
                              color: estadoChip.textColor,
                              fontWeight: 500,
                              borderRadius: 1.5,
                              minWidth: 90,
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            <Tooltip title="Imprimir PDF">
                              <IconButton
                                onClick={() => handleImprimirPDF(recibo.nroFormulario)}
                                size="small"
                                sx={{
                                  bgcolor: '#2196f3',
                                  color: '#fff',
                                  borderRadius: 1.5,
                                  width: 32,
                                  height: 32,
                                  '&:hover': { bgcolor: '#1976d2', transform: 'scale(1.1)' },
                                  transition: 'all 0.2s'
                                }}
                              >
                                <PrintIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {!esReciboAnulado(recibo) && (
                              <Tooltip title="Aplicar saldo / Ver aplicaciones">
                                <IconButton
                                  onClick={() => handleAbrirAplicarSaldo(recibo)}
                                  size="small"
                                  sx={{
                                    bgcolor: '#2e7d32',
                                    color: '#fff',
                                    borderRadius: 1.5,
                                    width: 32,
                                    height: 32,
                                    '&:hover': { bgcolor: '#1b5e20', transform: 'scale(1.1)' },
                                    transition: 'all 0.2s'
                                  }}
                                >
                                  <AccountBalanceWalletIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Más acciones">
                              <IconButton
                                size="small"
                                onClick={(e) => handleKebabOpen(e, recibo.nroFormulario)}
                                sx={{ borderRadius: 1.5, width: 32, height: 32 }}
                              >
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={totalRecords}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
                sx={{
                  borderTop: '1px solid #e0e0e0',
                }}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {pdfUrl && selectedRecibo && (
        <PDFViewer 
          pdfUrl={pdfUrl}
          onClose={handleClosePDF}
          title={`Recibo ${selectedRecibo}`}
          documentId={`${selectedRecibo}`}
          downloadFileName={pdfFileName ?? undefined}
        />
      )}

      <Menu
        anchorEl={kebabAnchor?.el}
        open={Boolean(kebabAnchor)}
        onClose={handleKebabClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {currentKebabRecibo && renderKebabItems(currentKebabRecibo)}
      </Menu>

      {aplicarDialogRecibo && (
        <AplicarSaldoReciboDialog
          open={aplicarDialogOpen}
          onClose={handleCloseAplicarDialog}
          reciboId={aplicarDialogRecibo.nroFormulario}
          nroCliente={aplicarDialogRecibo.nroCliente}
          nombreCliente={aplicarDialogRecibo.nombre || `Cliente ${aplicarDialogRecibo.nroCliente}`}
          reciboAnulado={esReciboAnulado(aplicarDialogRecibo)}
          onSuccess={() => fetchRecibos(page, rowsPerPage, searchTerm, fechaDesde, fechaHasta, filtroEstado, filtroNroSuc)}
        />
      )}
    </Box>
  );
};

export default Recibos; 